export class DiscoveryError extends Error {
  constructor(
    readonly type: "invalidEmail" | "discoveryFailed",
    message: string,
  ) {
    super(message);
    this.name = "DiscoveryError";
  }
}

// RFC 8620 autodiscovery, browser edition. DNS SRV is out of reach from a web
// page, so the ladder is: https://{domain}/.well-known/jmap, then
// https://mail.{domain}/.well-known/jmap. The probe is unauthenticated and
// redirect-following, so it works against both a public discovery document
// (like the static one key.store serves, which names the real JMAP origin in
// apiUrl) and a server that answers 401 directly (the final response URL then
// names the origin).
export async function discoverJmapOrigin(email: string): Promise<string> {
  const domain = email.split("@")[1]?.trim().toLowerCase();
  if (!domain || !domain.includes(".")) {
    throw new DiscoveryError("invalidEmail", "Enter a full email address like you@example.com.");
  }

  const failures: string[] = [];
  for (const origin of [`https://${domain}`, `https://mail.${domain}`]) {
    let response: Response;
    try {
      response = await fetch(`${origin}/.well-known/jmap`, {
        redirect: "follow",
        headers: { Accept: "application/json" },
      });
    } catch {
      failures.push(`${origin}: unreachable`);
      continue;
    }
    if (response.ok) {
      try {
        const body = (await response.json()) as { apiUrl?: string };
        if (body.apiUrl) {
          return new URL(body.apiUrl, response.url).origin;
        }
      } catch {
        failures.push(`${origin}: not a JMAP discovery document`);
        continue;
      }
      return new URL(response.url).origin;
    }
    if (response.status === 401) {
      // The session endpoint exists and wants credentials — that is the origin.
      return new URL(response.url).origin;
    }
    failures.push(`${origin}: HTTP ${response.status}`);
  }

  throw new DiscoveryError(
    "discoveryFailed",
    `Could not find a JMAP server for ${domain} (${failures.join("; ")}). Enter the server address under Advanced.`,
  );
}
